import { useState, useEffect, useMemo } from 'react';
import {
  Alert, Box, Button, Card, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, InputAdornment, MenuItem,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import loansApi from '../../../services/loansApi';
import brokerCompanyApi from '../../../services/brokerCompanyApi';
import brokerCompensationApi from '../../../services/brokerCompensationApi';
import { authService } from '../../../services/authService';
import FileUploadModal from '../../../components/shared/FileUploadModal';
import { Roles } from '../../../constants';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';
import {
  getPricingLoanTermOptions,
  isNonQmOnlyProductFamilies,
  mergeTermsForNonQmPricing,
  productFamiliesIncludeNonQm,
} from '../../../services/pricingEngineService';
import MismoAdvisorStep from './MismoAdvisorStep';
import { LOCK_DURATIONS, PRODUCT_FAMILIES } from '../../../constants/pricingOptions';

const LOAN_TYPES = {
  MORTGAGE: 'Mortgage',
  HELOC: 'HELOC',
  HOME_EQUITY_LOAN: 'HomeEquityLoan',
};

const NewLoanModal = ({ show, onClose, onLoanCreated, mismoImportAdvisorEnabled = false }) => {
  const [importMismo] = loansApi.endpoints.importMismo.useMutation();
  const [createLoan] = loansApi.endpoints.createLoan.useMutation();
  const [acceptMismoAdvisor] = loansApi.endpoints.acceptMismoAdvisor.useMutation();
  const [recommendMismoAdvisor] = loansApi.endpoints.recommendMismoAdvisor.useMutation();

  // ── Active compensation query (hooks must be at top, before any early returns) ──
  const { data: brokerCompany } = brokerCompanyApi.endpoints.getMyCompany.useQuery();
  // Prefetch active compensation so the filters step / pricing prefs have it warm.
  brokerCompensationApi.endpoints.getActiveCompensation.useQuery(
    brokerCompany?.id,
    { skip: !brokerCompany?.id }
  );

  // ── Current user role ──
  const currentUser = authService.getCurrentUser();
  const isProcessor = currentUser?.role === Roles.Processor;
  const isBroker = currentUser?.role === Roles.Broker || currentUser?.role === Roles.BrokerAdmin;

  // ── Company users for broker/processor dropdowns ──
  const { data: companyUsers = [] } = brokerCompanyApi.endpoints.getMyCompanyUsers.useQuery(
    undefined,
    { skip: !isProcessor && !isBroker }
  );
  const allBrokers = useMemo(() =>
    companyUsers.filter(u => (u.role === Roles.Broker || u.role === Roles.BrokerAdmin) && !u.inactive),
    [companyUsers]
  );
  const processors = useMemo(() =>
    companyUsers.filter(u => u.role === Roles.Processor && !u.inactive),
    [companyUsers]
  );

  // step: choose -> (upload -> confirm -> filters -> advisor) | manual
  // (filters/advisor only when the advisor flag is on)
  const [step, setStep] = useState('choose');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoanType, setImportLoanType] = useState(LOAN_TYPES.MORTGAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Manual creation fields
  const [manualFirstName, setManualFirstName] = useState('');
  const [manualLastName, setManualLastName] = useState('');

  const [lockDuration, setLockDuration] = useState(30);
  const [compensationType, setCompensationType] = useState('LPC');
  const [selectedFamilies, setSelectedFamilies] = useState(['Conventional']);
  const [selectedTerms, setSelectedTerms] = useState([30]);
  const [bpcCalcMethod, setBpcCalcMethod] = useState('PercentOfLoan');
  const [bpcValue, setBpcValue] = useState('');

  // Broker/Processor selection
  const [selectedBrokerUserId, setSelectedBrokerUserId] = useState('');
  const [selectedProcessorUserId, setSelectedProcessorUserId] = useState('');

  // Auto-select processor if only one exists
  useEffect(() => {
    if (isBroker && processors.length === 1) {
      setSelectedProcessorUserId(processors[0].userId);
    }
  }, [isBroker, processors]);

  const loanTermOptions = useMemo(
    () => getPricingLoanTermOptions(selectedFamilies),
    [selectedFamilies],
  );

  const applyNonQmMortgageType = isNonQmOnlyProductFamilies(selectedFamilies);

  useEffect(() => {
    if (!productFamiliesIncludeNonQm(selectedFamilies)) {
      setSelectedTerms((prev) => {
        const next = prev.filter((t) => t !== 40);
        return next.length > 0 ? next : [30];
      });
    }
  }, [selectedFamilies]);

  const ModalTitleWithClose = ({ children }) => (
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
      <span>{children}</span>
      <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
  );

  const handleClose = () => {
    setStep('choose');
    setSelectedFile(null);
    setImportLoanType(LOAN_TYPES.MORTGAGE);
    setManualFirstName('');
    setManualLastName('');
    setError(null);
    setLoading(false);
    setLockDuration(30);
    setCompensationType('LPC');
    setSelectedFamilies(['Conventional']);
    setSelectedTerms([30]);
    setBpcCalcMethod('PercentOfLoan');
    setBpcValue('');
    setSelectedBrokerUserId('');
    setSelectedProcessorUserId('');
    onClose();
  };

  const handleFileConfirm = (file) => {
    setSelectedFile(file);
    setStep('confirm');
  };

  /** Import the file and land the user on the Loan File tab in edit mode — no destination choice. */
  const handleImportSubmit = async () => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await importLoan();
      setLoading(false);
      onLoanCreated({
        id: result.loanId,
        loanNumber: result.loanNumber,
        action: 'edit',
        pricingFilters: applyNonQmMortgageType ? { applyNonQmMortgageType: true } : undefined,
      });
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import MISMO file'));
    }
  };

  const buildPricingFilters = () => ({
    lockDuration,
    compensationType,
    selectedFamilies,
    selectedTerms: mergeTermsForNonQmPricing(selectedTerms, selectedFamilies),
    applyNonQmMortgageType: isNonQmOnlyProductFamilies(selectedFamilies),
    ...(compensationType === 'BPC' ? { bpcCalcMethod, bpcValue } : {}),
  });

  const importLoan = async () => {
    const importPayload = {
      file: selectedFile,
      loanType: importLoanType,
      ...(selectedBrokerUserId && { brokerUserId: selectedBrokerUserId }),
      ...(selectedProcessorUserId && { processorUserId: selectedProcessorUserId }),
    };
    return importMismo(importPayload).unwrap();
  };

  const handleAdvisorAgree = async ({ assignPayload, alreadyImported }) => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const imported = alreadyImported || await importLoan();
      const loanId = imported.loanId;
      await acceptMismoAdvisor({
        loanId,
        pricingRequest: assignPayload.pricingRequest,
        productId: assignPayload.productId,
        rate: assignPayload.rate,
        price: assignPayload.price,
        lockTerm: assignPayload.lockTerm,
        ...(assignPayload.quoteId ? { quoteId: assignPayload.quoteId } : {}),
        ...(assignPayload.bpcCalcMethod
          ? { bpcCalcMethod: assignPayload.bpcCalcMethod, bpcValue: assignPayload.bpcValue }
          : {}),
      }).unwrap();
      const loanInfo = {
        id: loanId,
        loanNumber: imported.loanNumber,
        action: 'advisor-complete',
        pricingFilters: buildPricingFilters(),
      };
      handleClose();
      onLoanCreated(loanInfo);
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import and assign recommended product.'));
    }
  };

  const handleAdvisorDisagree = async ({ pricingFilters, preferredProductShortName, preferredRate, advisorOptions }) => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const imported = await importLoan();
      setLoading(false);
      onLoanCreated({
        id: imported.loanId,
        loanNumber: imported.loanNumber,
        action: 'price',
        pricingFilters: {
          ...pricingFilters,
          preferredProductShortName,
          preferredRate,
          advisorOptions,
        },
      });
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to import MISMO file.'));
    }
  };

  const handleImportAndLoadProducts = async ({ advisorOptions, confirmedFico }) => {
    if (isProcessor && !selectedBrokerUserId) {
      throw new Error('Please select a broker for the loan');
    }
    setError(null);
    setLoading(true);
    try {
      const imported = await importLoan();
      try {
        const recommend = await recommendMismoAdvisor({
          loanId: imported.loanId,
          lockTerm: advisorOptions?.lockTerm ?? lockDuration,
          compensationType: advisorOptions?.compensationType ?? compensationType,
          productFamilies: advisorOptions?.productFamilies ?? selectedFamilies,
          terms: mergeTermsForNonQmPricing(
            advisorOptions?.terms ?? selectedTerms,
            advisorOptions?.productFamilies ?? selectedFamilies,
          ),
          confirmedFico,
          targetPrice: advisorOptions?.targetPrice,
        }).unwrap();
        setLoading(false);
        return {
          loanId: imported.loanId,
          loanNumber: imported.loanNumber,
          products: recommend.pricingResponse?.products || [],
          pricingRequest: recommend.pricingRequest,
          pricingResponse: recommend.pricingResponse,
          recommendation: recommend.recommendation,
          analysis: recommend.analysis,
        };
      } catch (priceErr) {
        setLoading(false);
        return {
          loanId: imported.loanId,
          loanNumber: imported.loanNumber,
          products: [],
          pricingError: getAxiosErrorMessage(priceErr, 'Pricing failed after import. Enter product details manually.'),
        };
      }
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleManualCreate = async () => {
    if (isProcessor && !selectedBrokerUserId) {
      setError('Please select a broker for the loan');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await createLoan({
        borrowers: [{ firstName: manualFirstName.trim(), lastName: manualLastName.trim() }],
        loanType: importLoanType,
        ...(selectedBrokerUserId && { brokerUserId: selectedBrokerUserId }),
        ...(selectedProcessorUserId && { processorUserId: selectedProcessorUserId }),
      }).unwrap();
      setLoading(false);
      onLoanCreated({
        id: result.loanId,
        loanNumber: result.loanNumber,
        action: 'edit',
      });
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(getAxiosErrorMessage(err, 'Failed to create loan'));
    }
  };

  const toggleFilter = (arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  if (!show) return null;

  // ── File chip (reused in steps 2 & 3) ──
  const fileChip = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
      <InsertDriveFileIcon color="primary" sx={{ fontSize: 32 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={700}>{selectedFile?.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedFile?.size > 1048576
            ? `${(selectedFile.size / 1048576).toFixed(1)} MB`
            : `${(selectedFile?.size / 1024).toFixed(0)} KB`}
        </Typography>
      </Box>
      <Tooltip title="Change file">
        <IconButton size="small" onClick={() => setStep('upload')}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  // Step 0: How do you want to start?
  if (step === 'choose') {
    const optionCardSx = {
      p: 2.5,
      flex: 1,
      cursor: 'pointer',
      textAlign: 'center',
      transition: 'border-color 120ms, box-shadow 120ms',
      '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
    };
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan</ModalTitleWithClose>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How would you like to start?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Card variant="outlined" sx={optionCardSx} onClick={() => setStep('upload')}>
              <FileUploadIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>Import MISMO file</Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a MISMO 3.4 XML export and we&apos;ll fill in the application for you.
              </Typography>
            </Card>
            <Card variant="outlined" sx={optionCardSx} onClick={() => setStep('manual')}>
              <EditIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>Enter manually</Typography>
              <Typography variant="body2" color="text.secondary">
                Start with the borrower&apos;s name and complete the application on the Loan File.
              </Typography>
            </Card>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  // Manual path: borrower name + team, then create a blank loan and open the Loan File.
  if (step === 'manual') {
    const canCreate = manualFirstName.trim() && manualLastName.trim() && importLoanType
      && (!isProcessor || selectedBrokerUserId);
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan — Manual entry</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {/* mt keeps the floating labels from being clipped — DialogContent has no top padding after DialogTitle */}
          <Grid container spacing={2} sx={{ mb: 2, mt: 1.25 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Borrower First Name"
                required
                fullWidth
                size="small"
                value={manualFirstName}
                onChange={(e) => setManualFirstName(e.target.value)}
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Borrower Last Name"
                required
                fullWidth
                size="small"
                value={manualLastName}
                onChange={(e) => setManualLastName(e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            select
            label="Loan Type"
            required
            fullWidth
            size="small"
            value={importLoanType}
            onChange={(e) => setImportLoanType(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value={LOAN_TYPES.MORTGAGE}>Mortgage</MenuItem>
            <MenuItem value={LOAN_TYPES.HELOC} disabled>HELOC — Coming Soon</MenuItem>
            <MenuItem value={LOAN_TYPES.HOME_EQUITY_LOAN} disabled>Home Equity Loan — Coming Soon</MenuItem>
          </TextField>
          {isProcessor && (
            <TextField
              select
              label="Select Broker"
              required
              fullWidth
              size="small"
              value={selectedBrokerUserId}
              onChange={(e) => setSelectedBrokerUserId(e.target.value)}
              helperText="The broker who will originate this loan"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select a broker...</MenuItem>
              {allBrokers.map(b => (
                <MenuItem key={b.userId} value={b.userId}>
                  {b.firstName} {b.lastName}{b.nmls ? ` (NMLS: ${b.nmls})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isBroker && processors.length > 0 && (
            <TextField
              select
              label="Processor"
              fullWidth
              size="small"
              value={selectedProcessorUserId}
              onChange={(e) => setSelectedProcessorUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {processors.map(p => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" color="text.secondary">
            <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
            The loan opens on the Loan File where you can complete the application, then price when ready.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => { setError(null); setStep('choose'); }} disabled={loading}>Back</Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <EditIcon />}
            onClick={handleManualCreate}
            disabled={loading || !canCreate}
          >
            {loading ? 'Creating...' : 'Create Loan'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Step 1: File selection
  if (step === 'upload') {
    return (
      <FileUploadModal
        show
        onClose={handleClose}
        onConfirm={handleFileConfirm}
        title="New Loan — Select MISMO 3.4 XML"
        confirmLabel="Continue"
        accept=".xml"
        multiple={false}
        hint="MISMO 3.4 XML files only"
      />
    );
  }

  // Step 2: Loan type + team, then import straight to the Loan File.
  // (When the MISMO advisor flag is on, Continue goes to pricing preferences instead.)
  if (step === 'confirm') {
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {fileChip}
          <TextField
            select
            label="Loan Type"
            required
            fullWidth
            size="small"
            value={importLoanType}
            onChange={(e) => setImportLoanType(e.target.value)}
            helperText="This cannot be inferred from the XML and must be specified"
            sx={{ mb: 2 }}
          >
            <MenuItem value={LOAN_TYPES.MORTGAGE}>Mortgage</MenuItem>
            <MenuItem value={LOAN_TYPES.HELOC} disabled>HELOC — Coming Soon</MenuItem>
            <MenuItem value={LOAN_TYPES.HOME_EQUITY_LOAN} disabled>Home Equity Loan — Coming Soon</MenuItem>
          </TextField>
          {isProcessor && (
            <TextField
              select
              label="Select Broker"
              required
              fullWidth
              size="small"
              value={selectedBrokerUserId}
              onChange={(e) => setSelectedBrokerUserId(e.target.value)}
              helperText="The broker who will originate this loan"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select a broker...</MenuItem>
              {allBrokers.map(b => (
                <MenuItem key={b.userId} value={b.userId}>
                  {b.firstName} {b.lastName}{b.nmls ? ` (NMLS: ${b.nmls})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isBroker && processors.length > 0 && (
            <TextField
              select
              label="Processor"
              fullWidth
              size="small"
              value={selectedProcessorUserId}
              onChange={(e) => setSelectedProcessorUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {processors.map(p => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!mismoImportAdvisorEnabled && (
            <Typography variant="body2" color="text.secondary">
              <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
              The loan opens on the Loan File where you can review and edit everything, then price when ready.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setStep('upload')} disabled={loading}>Back</Button>
          {mismoImportAdvisorEnabled ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={!importLoanType}
              onClick={() => { setError(null); setStep('filters'); }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FileUploadIcon />}
              onClick={handleImportSubmit}
              disabled={loading || !importLoanType}
            >
              {loading ? 'Importing...' : 'Import Loan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  if (step === 'filters') {
    return (
      <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalTitleWithClose>New Loan — Pricing preferences</ModalTitleWithClose>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {fileChip}
          {isProcessor && (
            <TextField
              select
              label="Select Broker"
              required
              fullWidth
              size="small"
              value={selectedBrokerUserId}
              onChange={(e) => setSelectedBrokerUserId(e.target.value)}
              helperText="The broker who will originate this loan"
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select a broker...</MenuItem>
              {allBrokers.map(b => (
                <MenuItem key={b.userId} value={b.userId}>
                  {b.firstName} {b.lastName}{b.nmls ? ` (NMLS: ${b.nmls})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isBroker && processors.length > 0 && (
            <TextField
              select
              label="Processor"
              fullWidth
              size="small"
              value={selectedProcessorUserId}
              onChange={(e) => setSelectedProcessorUserId(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">None</MenuItem>
              {processors.map(p => (
                <MenuItem key={p.userId} value={p.userId}>
                  {p.firstName} {p.lastName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We will analyze your MISMO file and recommend a program and rate before creating the loan.
          </Typography>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 1.5 }}>Pricing filters</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  select label="Lock Term" fullWidth size="small"
                  value={lockDuration} onChange={(e) => setLockDuration(Number(e.target.value))}
                >
                  {LOCK_DURATIONS.map(d => (
                    <MenuItem key={d} value={d}>{d} days</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  select label="Compensation" fullWidth size="small"
                  value={compensationType} onChange={(e) => setCompensationType(e.target.value)}
                >
                  <MenuItem value="BPC">BPC</MenuItem>
                  <MenuItem value="LPC">LPC</MenuItem>
                </TextField>
              </Grid>
              {compensationType === 'BPC' && (
                <>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      select label="Calc Method" fullWidth size="small"
                      value={bpcCalcMethod} onChange={(e) => { setBpcCalcMethod(e.target.value); setBpcValue(''); }}
                    >
                      <MenuItem value="PercentOfLoan">Percent</MenuItem>
                      <MenuItem value="FixedAmount">Fixed Amount</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {bpcCalcMethod === 'FixedAmount' ? (
                      <CurrencyInput label="BPC $" value={bpcValue} onChange={setBpcValue} size="small" />
                    ) : (
                      <TextField
                        label="BPC %" type="number" fullWidth size="small"
                        value={bpcValue} onChange={(e) => setBpcValue(e.target.value)}
                        inputProps={{ step: 0.125, min: 0 }}
                      />
                    )}
                  </Grid>
                </>
              )}
            </Grid>
            <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap sx={{ mb: 1 }}>
              {PRODUCT_FAMILIES.map(pf => (
                <Button
                  key={pf.id} size="small"
                  variant={selectedFamilies.includes(pf.id) ? 'contained' : 'outlined'}
                  onClick={() => toggleFilter(selectedFamilies, setSelectedFamilies, pf.id)}
                >
                  {pf.label}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
              {loanTermOptions.map(lt => (
                <Button
                  key={lt} size="small"
                  variant={selectedTerms.includes(lt) ? 'contained' : 'outlined'}
                  onClick={() => toggleFilter(selectedTerms, setSelectedTerms, lt)}
                >
                  {lt}yr
                </Button>
              ))}
            </Stack>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => setStep('confirm')}>Back</Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            disabled={selectedFamilies.length === 0}
            onClick={() => { setError(null); setStep('advisor'); }}
          >
            Analyze & recommend
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!mismoImportAdvisorEnabled) {
    return null;
  }

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <ModalTitleWithClose>New Loan — Your recommendation</ModalTitleWithClose>
      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <MismoAdvisorStep
        selectedFile={selectedFile}
        lockDuration={lockDuration}
        compensationType={compensationType}
        selectedFamilies={selectedFamilies}
        selectedTerms={selectedTerms}
        bpcCalcMethod={bpcCalcMethod}
        bpcValue={bpcValue}
        onAgree={handleAdvisorAgree}
        onDisagree={handleAdvisorDisagree}
        onImportAndLoadProducts={handleImportAndLoadProducts}
        onBack={() => setStep('filters')}
        loading={loading}
      />
    </Dialog>
  );
};

export default NewLoanModal;
